import {
  ButtonAction,
  ButtonElement,
  ButtonInteractionType,
  CloseActionType,
} from "@redotech/redo-content-builder-common/element-schema";
import { BrandKitButton } from "@redotech/redo-web/brand-kit-button";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useCallback, useMemo } from "react";
import { executeCloseActions } from "../spotlight/close-action-executor";
import { getBaseElementStyles } from "./base-element-styles";
import { ElementBox } from "./element-box";
import { FormElementProps } from "./index";

// True when a close button has no useful behavior at storefront-render
// time (no `onClose` wired and no fallback action like CLOSE_AND_ACTION /
// OPEN_LINK — typical for embedded contexts that have no modal to
// dismiss). Builder previews always render close buttons regardless so
// merchants can see and configure them. Used by FormButton to
// short-circuit, and by FormContainer to skip wrapping it in an empty
// Flex row that would otherwise render as a 0-height phantom slot.
export function shouldHideFormButton(
  element: ButtonElement,
  onClose: (() => void) | undefined,
  isBuilder: boolean | undefined,
): boolean {
  if (isBuilder) {
    return false;
  }
  const interaction = element.interaction;
  return (
    element.action === ButtonAction.CLOSE &&
    !onClose &&
    interaction?.interactionType !== ButtonInteractionType.CLOSE_AND_ACTION &&
    interaction?.interactionType !== ButtonInteractionType.OPEN_LINK
  );
}

interface FormButtonProps extends Pick<
  FormElementProps<ButtonElement>,
  | "element"
  | "brandKit"
  | "brandKitOverrides"
  | "onClose"
  | "submitting"
  | "closeActionContext"
  | "isBuilder"
  | "onWillSubmit"
  | "registerPendingCloseActions"
> {}

export const FormButton = memo(function FormButton({
  element,
  brandKit,
  brandKitOverrides,
  submitting,
  onClose,
  closeActionContext,
  isBuilder,
  onWillSubmit,
  registerPendingCloseActions,
}: FormButtonProps) {
  const interaction = element.interaction;

  const shouldHide = shouldHideFormButton(element, onClose, isBuilder);

  const isSubmitButton =
    interaction?.interactionType === ButtonInteractionType.GO_TO_NEXT ||
    interaction?.interactionType === ButtonInteractionType.GO_TO_STEP ||
    element.action === ButtonAction.SUBMIT_AND_ADVANCE ||
    element.action === ButtonAction.SUBMIT_AND_GO_TO_URL;

  const handleClick = useCallback(async () => {
    if (interaction) {
      switch (interaction.interactionType) {
        case ButtonInteractionType.CLOSE:
          onClose?.();
          return;
        case ButtonInteractionType.CLOSE_AND_ACTION:
          // When this button also submits the form, defer the close actions
          // to the form's onSubmit handler so they run AFTER the submit
          // resolves the discount. Running them inline here would race the
          // submit and apply a stale (likely empty) resolvedDiscounts.
          if (isSubmitButton) {
            registerPendingCloseActions?.(interaction.actions);
            return;
          }
          await executeCloseActions(
            interaction.actions,
            closeActionContext ?? {},
          );
          onClose?.();
          return;
        case ButtonInteractionType.OPEN_LINK:
          if (interaction.openInNewTab) {
            window.open(interaction.url, "_blank", "noopener,noreferrer");
          } else {
            window.location.href = interaction.url;
          }
          return;
        case ButtonInteractionType.GO_TO_NEXT:
        case ButtonInteractionType.GO_TO_STEP:
          return;
      }
    }

    if (element.action === ButtonAction.SUBMIT_AND_GO_TO_URL) {
      onWillSubmit?.(element.destinationUrl);
      return;
    }

    if (onClose && element.action === ButtonAction.CLOSE) {
      onClose();
    }
  }, [
    interaction,
    onClose,
    closeActionContext,
    element.action,
    element.destinationUrl,
    onWillSubmit,
    isSubmitButton,
    registerPendingCloseActions,
  ]);

  const subtext = useMemo(() => {
    if (!closeActionContext?.buttonSubtext) {
      return undefined;
    }
    if (
      interaction?.interactionType !== ButtonInteractionType.CLOSE_AND_ACTION
    ) {
      return undefined;
    }
    // Only OPEN_CHECKOUT counts here. A CLICK_ELEMENT button may also end up at
    // checkout (e.g. clicking the merchant's add-to-cart), but we can't know
    // that statically, so its subtext is intentionally not shown rather than
    // risk showing checkout copy for a click that doesn't go to checkout.
    const hasCheckoutAction = interaction.actions.some(
      (a) => a.type === CloseActionType.OPEN_CHECKOUT,
    );
    return hasCheckoutAction ? closeActionContext.buttonSubtext : undefined;
  }, [closeActionContext?.buttonSubtext, interaction]);

  if (shouldHide) {
    return null;
  }

  const hasBaseStyles =
    element.backgroundColor !== undefined ||
    element.borderWidth !== undefined ||
    element.padding !== undefined;

  const buttonElement = (
    <BrandKitButton
      brandKit={brandKit}
      disabled={isBuilder}
      elementOverride={element.styleOverride}
      globalOverride={
        brandKitOverrides?.buttons.enabled
          ? brandKitOverrides?.buttons
          : undefined
      }
      hierarchy={element.hierarchy}
      onClick={handleClick}
      pending={submitting}
      text={element.text}
      type={isSubmitButton ? "submit" : "button"}
    />
  );

  const content = subtext ? (
    // design-allow: the subtext stack spans the button box
    <Flex dir="column" gap="xs" style={{ width: "100%" }}>
      {buttonElement}
      {/* design-allow: centers the subtext across the button box */}
      <Flex justify="center" style={{ width: "100%" }}>
        <Text fontSize="xs" fontWeight="regular" textColor="secondary">
          {subtext}
        </Text>
      </Flex>
    </Flex>
  ) : (
    buttonElement
  );

  return (
    <ElementBox
      alignment={element.contentAlignment}
      bare={!hasBaseStyles}
      fillsWidth
      // design-allow: merchant-authored element styles have no tokens
      style={{ ...getBaseElementStyles(element), width: "100%" }}
    >
      {content}
    </ElementBox>
  );
});
